import React, { useState } from "react";
import { Row, Col, CardTitle, Spinner } from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";

import { AUTOPILOT_COLORS } from "../autopilot/styles";

const CustomTable = ({
  data,
  columns,
  fileName,
  tableName,
  loading,
  searchPlaceholder = "Buscar na tabela...",
  clearSearchLabel = "Limpar",
  sizePerPage = 5,
  tableClasses = "text-center",
  wrapperClasses = "table-responsive",
  rowEvents,
  rowStyle,
  rowClasses,
  showToolbar = true,
  fillHeight = false,
  themedPagination = false,
  rootClassName = "",
  rootStyle,
}) => {
  const [searchText, setSearchText] = useState("");
  const shellClassName = [
    "custom-table-shell",
    fillHeight ? "custom-table-shell--fill" : "",
    themedPagination ? "custom-table-shell--themed-pagination" : "",
    rootClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const customTotal = (from, to, size) => (
    <span
      className="react-bootstrap-table-pagination-total"
      style={{ color: AUTOPILOT_COLORS.muted, fontWeight: 500 }}
    >
      Mostrando {from} a {to} de {size} resultados
    </span>
  );

  const paginationOptions = {
    sizePerPage,
    pageStartIndex: 1,
    alwaysShowAllBtns: true,
    withFirstAndLast: true,
    hideSizePerPage: true,
    firstPageText: "Primeira",
    prePageText: "Voltar",
    nextPageText: "Próximo",
    lastPageText: "Última",
    nextPageTitle: "Próxima página",
    prePageTitle: "Página anterior",
    firstPageTitle: "Primeira página",
    lastPageTitle: "Última página",
    showTotal: true,
    paginationTotalRenderer: customTotal,
  };

  const orderIcon = (order) => {
    if (!order)
      return (
        <span>
          &nbsp;&nbsp;
          <i className="tim-icons icon-minimal-down" />
          <i className="tim-icons icon-minimal-up" />
        </span>
      );
    if (order === "asc")
      return (
        <span>
          &nbsp;
          <span style={{ color: AUTOPILOT_COLORS.accent }}>
            <i className="tim-icons icon-minimal-up" />
          </span>
        </span>
      );
    if (order === "desc")
      return (
        <span>
          &nbsp;
          <span style={{ color: AUTOPILOT_COLORS.accent }}>
            <i className="tim-icons icon-minimal-down" />
          </span>
        </span>
      );
    return null;
  };

  const columnsFormatted = columns.map((col) => ({
    ...col,
    sort: col.sort !== false,
    hidden: col.hidden || col.dataField === "id",
    sortCaret: (order, column) => {
      return orderIcon(order, column);
    },
  }));

  const filteredData = searchText
    ? data.filter((row) =>
        columnsFormatted.some((col) => {
          if (col.hidden) return false;
          const val = row[col.dataField];
          return val != null && String(val).toLowerCase().includes(searchText.toLowerCase());
        })
      )
    : data;

  const handleExportCSV = () => {
    const visibleCols = columnsFormatted.filter((c) => !c.hidden && c.csvExport !== false);
    const header = visibleCols.map((c) => `"${String(c.text).replace(/"/g, '""')}"`).join(",");
    const rows = filteredData.map((row) =>
      visibleCols.map((c) => {
        const val = row[c.dataField];
        const str = val == null ? "" : String(val);
        return `"${str.replace(/"/g, '""')}"`;
      }).join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName || "export"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div
        className="custom-table-loading"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.6rem",
          padding: "2rem 1rem",
          color: AUTOPILOT_COLORS.muted,
          fontSize: "0.9rem",
        }}
      >
        <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
        <span>Carregando…</span>
      </div>
    );
  }

  return (
    <div className={shellClassName} style={rootStyle}>
      {showToolbar && (
        <>
          <Row style={{ alignItems: "center", rowGap: "0.75rem", marginBottom: "0.5rem" }}>
            <Col xl="5" lg="5" md="12">
              <CardTitle tag="h4" style={{ marginBottom: 0 }}>
                <i className="tim-icons icon-paper" /> {tableName}
              </CardTitle>
            </Col>
            <Col xl="7" lg="7" md="12">
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "0.75rem",
                }}
              >
                <div style={{ display: "inline-flex", alignItems: "center", position: "relative" }}>
                  <i
                    className="tim-icons icon-zoom-split"
                    style={{
                      position: "absolute",
                      left: 10,
                      color: AUTOPILOT_COLORS.muted,
                      fontSize: 14,
                      pointerEvents: "none",
                    }}
                  />
                  <input
                    type="text"
                    className="form-control"
                    style={{
                      width: 260,
                      maxWidth: "100%",
                      paddingLeft: 32,
                      background: AUTOPILOT_COLORS.inputBg,
                      border: `1px solid ${AUTOPILOT_COLORS.inputBorder}`,
                      color: AUTOPILOT_COLORS.title,
                      borderRadius: 6,
                    }}
                    placeholder={searchPlaceholder}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
                <button
                  className="btn btn-danger"
                  style={{ margin: 0 }}
                  onClick={() => setSearchText("")}
                >
                  {clearSearchLabel}
                </button>
                <button
                  className="btn btn-success"
                  style={{ margin: 0 }}
                  onClick={handleExportCSV}
                >
                  Exportar CSV
                </button>
              </div>
            </Col>
          </Row>
          <hr />
        </>
      )}
      <BootstrapTable
        keyField="id"
        data={filteredData}
        columns={columnsFormatted}
        noDataIndication="Consulta não retornou dados, altere a consulta para obter um novo resultado."
        bordered={false}
        striped
        classes={tableClasses}
        wrapperClasses={wrapperClasses}
        pagination={paginationFactory(paginationOptions)}
        rowEvents={rowEvents}
        rowStyle={rowStyle}
        rowClasses={rowClasses}
      />
    </div>
  );
};

export default CustomTable;
